import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputAdornment,
  Box,
  Typography,
  ListSubheader,
  CircularProgress
} from '@mui/material';

const FormField = ({ 
  name,
  label,
  type = 'text',
  value,
  onChange,
  error = false,
  helperText,
  required = false,
  icon,
  multiline = false,
  rows = 4,
  inputProps = {},
  select = false,
  disabled = false,
  placeholder,
  options = [],
  loading = false,
  ...props 
}) => {
  // Ensure label is always a valid string - aggressive safety
  const rawLabel = label || name || '';
  const safeLabel = (typeof rawLabel === 'string' ? rawLabel : String(rawLabel)).trim() || 'Field';
  const rawPlaceholder = placeholder || safeLabel || '';
  const safePlaceholder = (typeof rawPlaceholder === 'string' ? rawPlaceholder : String(rawPlaceholder)).trim();
  
  const handleChange = (event) => {
    const newValue = type === 'checkbox' ? event.target.checked : event.target.value;
    onChange(name, newValue);
  };

  // Remove custom props that shouldn't be passed to DOM elements
  const { 
    size, 
    loadOptions, 
    dependsOn, 
    conditional, 
    onChange: fieldOnChange,
    ...cleanProps 
  } = props;

  // Handle checkbox type separately
  if (type === 'checkbox') {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: error ? 'error.main' : 'grey.300',
        backgroundColor: 'grey.50',
        '&:hover': {
          borderColor: error ? 'error.main' : 'primary.main',
          backgroundColor: 'background.paper'
        },
        transition: 'all 0.3s ease'
      }}>
        <FormControlLabel
          control={
            <Checkbox
              name={name}
              checked={Boolean(value)}
              onChange={handleChange}
              disabled={disabled}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight={500}>
                {safeLabel}
                {required && <span style={{ color: '#d32f2f' }}> *</span>}
              </Typography>
              {helperText && (
                <Typography variant="body2" color="text.secondary">
                  {helperText}
                </Typography>
              )}
            </Box>
          }
        />
        {error && (
          <FormHelperText error sx={{ mt: 0 }}>
            {error}
          </FormHelperText>
        )}
      </Box>
    );
  }

    // Handle select type
    if (select || type === 'select') {
    
    const currentValue = value || '';

    const optionMatchesValue = (opt, target) => {
      const optValue = typeof opt === 'object' ? opt.value : opt;
      if (String(optValue) === String(target)) {
        return true;
      }
      if (opt && typeof opt === 'object' && Array.isArray(opt.options)) {
        return opt.options.some((sub) => optionMatchesValue(sub, target));
      }
      return false;
    };

    // Check if the current value exists in options (including nested template groups)
    const valueExistsInOptions =
      options.length > 0 && options.some((opt) => optionMatchesValue(opt, currentValue));
    
    // Determine safe value to prevent MUI warnings
    // - Αν φορτώνουν options, κράτα το τρέχον value (αλλιώς το Select γίνεται '' και χάνονται κατανομές στο submit)
    // - Αν options άδεια αλλά υπάρχει value: κράτα το value (προσωρινό MenuItem)
    // - Αν options φορτώθηκαν και το value υπάρχει στη λίστα: κράτα το
    // - Αλλιώς: κενό (άκυρο value)
    const safeValue = loading
      ? currentValue !== '' && currentValue !== null && currentValue !== undefined
        ? currentValue
        : ''
      : options.length === 0 && currentValue
        ? currentValue
        : valueExistsInOptions
          ? currentValue
          : '';
    
    // Προσωρινό MenuItem ώστε το Select να έχει έγκυρο value όσο options=[] (και κατά τη φόρτωση)
    const needsTempOption = options.length === 0 && currentValue;
    
    return (
      <FormControl fullWidth error={Boolean(error)} disabled={disabled}>
        {safeLabel && (
          <InputLabel required={required}>
            {safeLabel}
          </InputLabel>
        )}
        <Select
          name={name}
          value={safeValue}
          onChange={(event) => {
            handleChange(event);
          }}
          label={safeLabel}
          disabled={disabled || loading}
          startAdornment={icon && (
            <InputAdornment position="start">
              {icon}
            </InputAdornment>
          )}
          {...inputProps}
          {...cleanProps}
        >
          {loading && (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Φόρτωση...
              </Box>
            </MenuItem>
          )}
          {needsTempOption && (
            <MenuItem value={currentValue} disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Φόρτωση...
              </Box>
            </MenuItem>
          )}
          {!loading && options.map((option) => {
            // Check if this option has nested options (optgroup)
            if (option.options && Array.isArray(option.options)) {
              return [
                <ListSubheader 
                  key={`header-${option.value}`}
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    backgroundColor: 'grey.50',
                    fontSize: '0.875rem'
                  }}
                >
                  {option.label}
                </ListSubheader>,
                <MenuItem
                  key={`type-${option.value}`}
                  value={option.value}
                  sx={{ pl: 2, fontWeight: 500 }}
                >
                  {option.label}
                </MenuItem>,
                ...option.options.map((subOption) => (
                  <MenuItem 
                    key={subOption.value} 
                    value={subOption.value}
                    sx={{ pl: 3 }}
                  >
                    {subOption.label}
                  </MenuItem>
                ))
              ];
            }
            
            // Regular option
            return (
              <MenuItem 
                key={typeof option === 'object' ? option.value : option} 
                value={typeof option === 'object' ? option.value : option}
              >
                {typeof option === 'object' ? option.label : option}
              </MenuItem>
            );
          })}
        </Select>
        {(error || helperText) && (
          <FormHelperText>
            {error || helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  // Handle regular text fields
  return (
    <TextField
      fullWidth
      name={name}
      label={safeLabel}
      type={type}
      value={value || ''}
      onChange={handleChange}
      error={Boolean(error)}
      helperText={error || helperText}
      required={required}
      disabled={disabled}
      placeholder={safePlaceholder}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      InputProps={{
        startAdornment: icon && (
          <InputAdornment position="start">
            {icon}
          </InputAdornment>
        ),
        ...inputProps
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          '&:hover fieldset': {
            borderColor: 'primary.main',
          },
        },
      }}
      {...cleanProps}
    />
  );
};

export default FormField;
